import React from "react";
import {
  Select as UISelect,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@gettactic/components/src/@v2/components/ui/select";
import { FormControlWrapper } from "@gettactic/components/src/form/FormControlWrapper";
import { FormControlOutline } from "@gettactic/components/src/form/FormControlWrapper/FormControlOutline";
import { useFormControlWrapper } from "@gettactic/components/src/form/FormControlWrapper/FormControlWrapperProvider";
import { splitFormControlWrapperProps } from "@gettactic/components/src/form/FormControlWrapper/helpers";
import { TypeSelectProps, FormTypeSelectProps } from "./types";
import { useWorkplaceRequestTypes } from "@/hooks/useWorkplaceRequestTypes";
import { WorkplaceRequestTypeElement } from "@gettactic/api";
import { cn } from "@gettactic/helpers";
import { useController } from "react-hook-form";
import Select, { components } from "react-select";
import { Plus } from "lucide-react";
import { Label } from "@radix-ui/react-label";

// Internal component that handles the select input without FormControlWrapper
const WRTypeSelectInput = React.forwardRef<
  HTMLButtonElement,
  Omit<TypeSelectProps, "form" | "name">
>((props, ref) => {
  const {
    className,
    types: propTypes,
    isLoading: propIsLoading,
    ...rest
  } = props;
  const { id } = useFormControlWrapper();

  // Fetch types from API if not provided as props
  const { allElements: apiTypes, isFetching: apiIsLoading } =
    useWorkplaceRequestTypes();

  // Use prop types if provided, otherwise use API types
  const types = propTypes || apiTypes;
  const isLoading = propIsLoading !== undefined ? propIsLoading : apiIsLoading;

  const fallbackTypes: WorkplaceRequestTypeElement[] = [];

  const displayTypes = types && types.length > 0 ? types : fallbackTypes;

  return (
    <UISelect value={rest.value} onValueChange={rest.onValueChange}>
      <SelectTrigger
        ref={ref}
        className={cn("h-8 min-h-8 border-0 bg-transparent shadow-none", className)}
        id={id}
        disabled={rest.disabled}
      >
        <SelectValue
          placeholder={isLoading ? "Loading..." : rest.placeholder}
          className="text-left"
        />
      </SelectTrigger>
      {displayTypes.length > 0 && (
        <SelectContent className="z-dropdown" position="popper" sideOffset={4}>
          {displayTypes.map((type) => (
            <SelectItem key={type.id} value={type.id}>
              {type.name}
            </SelectItem>
          ))}
        </SelectContent>
      )}
    </UISelect>
  );
});

WRTypeSelectInput.displayName = "WRTypeSelectInput";

export const HookFormWRTypeSelect = React.forwardRef<
  HTMLButtonElement,
  FormTypeSelectProps<any>
>((props, ref) => {
  const { form, name, rules, ...rest } = props;

  const {
    field: { value, onChange },
    fieldState: { error }
  } = useController({
    name,
    control: form.control,
    rules
  });

  const { enableSlotsInputFocus = true, ...restProps } = rest;
  const [wrapperProps, inputProps] = splitFormControlWrapperProps({
    ...restProps,
    error: error?.message,
    isInvalid: !!error
  });

  return (
    <FormControlWrapper
      {...wrapperProps}
      enableSlotsInputFocus={enableSlotsInputFocus}
    >
      <WRTypeSelectInput
        ref={ref}
        value={value}
        onValueChange={onChange}
        {...inputProps}
      />
      <FormControlOutline />
    </FormControlWrapper>
  );
});

HookFormWRTypeSelect.displayName = "HookFormWRTypeSelect";

export const ReservationTypeSelect: React.FC<{
  onTypeSelect: (typeId: string) => void;
  selectedTypes: string[];
  className?: string;
}> = ({ onTypeSelect, selectedTypes, className }) => {
  const { allElements: types, isFetching: isLoading } =
    useWorkplaceRequestTypes();

  // Filter out already selected types
  const availableTypes = types.filter(
    (type) => !selectedTypes.includes(type.id)
  );

  const options = availableTypes.map((type) => ({
    label: type.name,
    value: type.id,
    description: type.description
  }));

  const selectId = "workplace-request-type-select";

  const placeholder = "Add request";

  // Custom dropdown indicator with plus icon
  const DropdownIndicator = (props: any) => {
    return (
      <components.DropdownIndicator {...props}>
        <Plus className="h-4 w-4" />
      </components.DropdownIndicator>
    );
  };

  return (
    <div className={cn("flex items-center justify-between gap-1", className)}>
      <div>
        <Label htmlFor={selectId} className="font-inter font-normal text-sm">
          Add Workplace Request(s)
        </Label>
      </div>
      <Select
        inputId={selectId}
        value={null}
        placeholder={isLoading ? "Loading..." : placeholder}
        options={options}
        onChange={(option) => {
          if (option) {
            onTypeSelect(option.value);
          }
        }}
        isDisabled={isLoading || availableTypes.length === 0}
        noOptionsMessage={() => "No more request types available"}
        className="min-w-[120px] border-1 bg-white"
        classNamePrefix="react-select"
        styles={{
          control: (provided) => ({
            ...provided,
            marginTop: "3px" // 3px from the label line height
          })
        }}
        components={{
          DropdownIndicator,
          Option: (props) => (
            <components.Option {...props}>
              <div>
                <div className="font-medium">{props.data?.label}</div>
                {props.data?.description && (
                  <div className="text-muted-foreground text-sm">
                    {props.data.description}
                  </div>
                )}
              </div>
            </components.Option>
          )
        }}
      />
    </div>
  );
};
